import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect, Provider } from 'react-redux';
import { RouterProps } from 'react-router';
import {
  BrowserRouter as Router,
  Redirect,
  Switch,
  withRouter
} from 'react-router-dom';
import { PersistGate } from 'redux-persist/lib/integration/react';
import { makeStyles } from 'tss-react/mui';
import NodeServer from './common/node_server';
import ErrorBoundary from './components/error-boundary/Root';
import { IPermissions } from './models/auth';
import { ProtectedRoute, Route } from './Router';
import { default as config, IConfig } from './services/config';
import './services/sentry';
import * as authActions from './store/actions/auth';
import * as websiteActions from './store/actions/website';
import { IAppState } from './store/reducers';
import { getPermissions } from './store/selectors/user';
import { AppDispatch, persistor, store } from './store/store';
import theme from './theme';

declare global {
  interface Window {
    io?: any;
    permissions?: any;
    Components?: any;
    dispatch: (action) => any;
    routerHistory?: any;
    rwis: {
      config: IConfig;
    };
  }
}

// Remove once redux is upgraded
interface ConnectedReduxProps<S> {
  dispatch: AppDispatch;
}

interface IPageProps
  extends IAppState,
    RouterProps,
    ConnectedReduxProps<IAppState> {
  permissions: IPermissions;
}
const Fallback = () => (
  <Grid container textAlign='center'>
    <Grid size={12}>
      <CircularProgress color='primary' size={100} />
    </Grid>
  </Grid>
);

const AccountDetails = React.lazy(() => import('./containers/AccountDetails'));
const Acknowledgements = React.lazy(
  () => import('./containers/Acknowledgements')
);
const Batches = React.lazy(() => import('./containers/Batches'));
const Blocks = React.lazy(() => import('./containers/Blocks'));
const SearchCalls = React.lazy(() => import('./containers/Calls'));
const CommissaryManage = React.lazy(
  () => import('./containers/CommissaryManage')
);
const CommissaryOrders = React.lazy(
  () => import('./containers/CommissaryOrders')
);
const ControlCenter = React.lazy(() => import('./containers/ControlCenter'));
const InmateDetails = React.lazy(() => import('./containers/InmateDetails'));
const InmateNotifications = React.lazy(
  () => import('./containers/InmateNotification')
);
const InmateRequests = React.lazy(() => import('./containers/InmateRequests'));
const Inmates = React.lazy(() => import('./containers/Inmates'));
const Login = React.lazy(() => import('./containers/Login'));
const NotFound = React.lazy(() => import('./containers/NotFound'));
const PrivateNumbers = React.lazy(() => import('./containers/PrivateNumbers'));
const Profile = React.lazy(() => import('./containers/Profile'));
const SearchTexts = React.lazy(() => import('./containers/SearchTexts'));
const SearchPostalMail = React.lazy(
  () => import('./containers/SearchPostalMail')
);
const SearchVisitations = React.lazy(
  () => import('./containers/SearchVisitations')
);
const SearchVoicemail = React.lazy(
  () => import('./containers/SearchVoicemail')
);
const Users = React.lazy(() => import('./containers/Users'));
const VisitationManage = React.lazy(
  () => import('./containers/VisitationManage')
);
const LiveStatus = React.lazy(
  () => import('./components/live-status/LiveStatus')
);
const SiteMessage = React.lazy(() => import('./components/header/SiteMessage'));
const PageNotification = React.lazy(
  () => import('./components/layout/PageNotification')
);
const Header = React.lazy(() => import('./components/header/Header'));
const Contact = React.lazy(() => import('./containers/Contact'));
const EventNotifications = React.lazy(
  () => import('./containers/EventNotifications')
);
const HousingUnits = React.lazy(() => import('./containers/HousingUnits'));
const PersonSearch = React.lazy(() => import('./containers/PersonSearch'));
const ReferenceMaterials = React.lazy(
  () => import('./containers/ReferenceMaterials')
);
const Reports = React.lazy(() => import('./containers/Reports'));
const useStyles = makeStyles()({
  content: {
    [`@media print`]: {
      paddingLeft: '0 !important'
    }
  }
});

const Page: React.FC<IPageProps> = (props) => {
  const [navWidth, setNavWidth] = useState<number>();
  const { classes } = useStyles();

  useEffect(() => {
    // Retrieve the base website data
    if (
      props.common.rehydrated &&
      (props.common.facility == null ||
        props.common.facility.communicationsEnabled === undefined)
    ) {
      props.dispatch(websiteActions.loadBaseData());
    }

    // Start the node server connection
    NodeServer.get();
  }, []);

  let initialLoggedInRoute = '/search/calls';

  if (!props.permissions.hasPermission('search')) {
    initialLoggedInRoute = '/inmates';
  } else if (
    !config.hasOneOfFeatures([
      'audio-call',
      'video-call',
      'wireless-audio-calls'
    ])
  ) {
    if (config.hasFeature('texting')) {
      initialLoggedInRoute = '/search/texts';
    } else if (config.hasOneOfFeatures(['visitation', 'audio-visitation'])) {
      initialLoggedInRoute = '/search/visitation';
    } else if (config.hasFeature('voicemail')) {
      initialLoggedInRoute = '/search/voicemail';
    } else {
      initialLoggedInRoute = '/inmates';
    }
  }

  return (
    <React.Suspense fallback={Fallback()}>
      <CssBaseline />
      <PageNotification />

      {props.siteMessage.active && (
        <SiteMessage
          message={props.siteMessage.message}
          severity={props.siteMessage.severity}
        />
      )}

      {props.auth.loggedIn && (
        <Header
          name={config.name}
          facility={props.common.facility}
          user={props.auth.facilityUser}
          permissions={props.permissions}
          onWidthChange={setNavWidth}
          onLogout={() => {
            props.dispatch(authActions.logout());
          }}
        />
      )}

      <React.Suspense fallback={Fallback()}>
        <div
          style={{ paddingLeft: props.auth.loggedIn ? navWidth : 0 }}
          className={classes.content}>
          <Switch>
            <Redirect exact from='/' to={initialLoggedInRoute} />
            <Route exact path='/login' component={Login} />
            <ProtectedRoute path='/search/calls' component={SearchCalls} />
            <ProtectedRoute
              path='/search/voicemail'
              component={SearchVoicemail}
            />
            <ProtectedRoute path='/search/texts' component={SearchTexts} />
            <ProtectedRoute
              path='/search/mail-connect'
              component={SearchPostalMail}
            />
            <ProtectedRoute
              path='/search/visitations'
              component={SearchVisitations}
            />
            <ProtectedRoute
              path='/inmates/details/:account'
              component={InmateDetails}
            />
            <ProtectedRoute path='/account/:ani' component={AccountDetails} />
            <ProtectedRoute path='/inmates' component={Inmates} />
            <ProtectedRoute
              path='/control-center/:subPage?'
              component={ControlCenter}
            />
            <ProtectedRoute path='/private' component={PrivateNumbers} />
            <ProtectedRoute path='/blocks' component={Blocks} />
            <ProtectedRoute
              path='/inmate-notifications'
              component={InmateNotifications}
            />
            <ProtectedRoute
              path='/inmate-requests/:requestId?'
              component={InmateRequests}
            />
            <ProtectedRoute path='/batches/:batchId?' component={Batches} />
            <ProtectedRoute path='/live-status' component={LiveStatus} />
            <ProtectedRoute
              path='/visitations/manage'
              component={VisitationManage}
            />
            <ProtectedRoute path='/users' component={Users} />
            <ProtectedRoute path='/profile' component={Profile} />
            <ProtectedRoute
              path='/commissary/manage/:categoryId?'
              component={CommissaryManage}
            />
            <ProtectedRoute
              path='/commissary/orders'
              component={CommissaryOrders}
            />
            <ProtectedRoute
              path='/acknowledgements'
              component={Acknowledgements}
            />
            <ProtectedRoute path='/contact' component={Contact} />
            <ProtectedRoute path='/alerts' component={EventNotifications} />
            <ProtectedRoute path='/housing-units' component={HousingUnits} />
            <ProtectedRoute path='/person-search' component={PersonSearch} />
            <ProtectedRoute path='/reports' component={Reports} />
            <ProtectedRoute
              path='/reference-materials'
              component={ReferenceMaterials}
            />
            <Route path='*' component={NotFound} />
          </Switch>
        </div>
      </React.Suspense>
    </React.Suspense>
  );
};

const ConnectedPage = withRouter(
  connect((state: IAppState) => ({
    ...state,
    permissions: getPermissions(state)
  }))(Page)
);

// If the <head> block contains a <base> tag, let's respect that as the base URL for the router
let baseHref = '';
if (document.getElementById('base-href')) {
  baseHref = document
    .getElementById('base-href')
    .getAttribute('href')
    .slice(0, -1);
}

export const muiCache = createCache({
  key: 'mui',
  prepend: false
});

const App = () => (
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Router basename={baseHref}>
          <CacheProvider value={muiCache}>
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <ConnectedPage />
              </LocalizationProvider>
            </ThemeProvider>
          </CacheProvider>
        </Router>
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

export default App;
